/**
 * Simple Auth interceptor for JWT token refresh
 */
import router from '../router';
import { refreshToken, setLoggingOut } from '../services/authService';
import store from '../store';

// Make router globally accessible for route checks
window.router = router;

// Store original fetch
const originalFetch = window.fetch;

// Flag to prevent redirecting to login multiple times
let isRedirectingToLogin = false;

// Flag to track if we're already handling a token refresh
let isRefreshingToken = false;

// Flag to track if user is being logged out
let isLoggingOut = false;

/**
 * Setup a simple fetch interceptor to handle token refresh
 */
export function setupAuthInterceptor() {
	// Clear any stale session flags on initialization
	sessionStorage.removeItem('logging_out');
	sessionStorage.removeItem('auth_failed');
	sessionStorage.removeItem('auth_check_in_progress');
	// Override the fetch function
	window.fetch = async (url, options = {}) => {

		// Prefer explicit API base URL when provided (works for www -> apex domain)
		let baseUrl = process.env.VUE_APP_API_BASE_URL || '';
		if (!baseUrl || baseUrl === '') {
			baseUrl = 'https://keepthemalive.ai';
		}
		if (baseUrl.endsWith('/')) {
			baseUrl = baseUrl.slice(0, -1);
		}
		if (url.startsWith('/')) {
			url = `${baseUrl}${url}`;
		}

		// Add credentials to include cookies by default
		options.credentials = options.credentials || 'include';
		
		// Check if we're in the process of logging out
		// Use memory flag instead of sessionStorage to prevent issues between page loads
		const loggingOut = isLoggingOut;
		
		// Skip intercepting the refresh token request itself
		if (url.includes('/api/auth/refresh')) {
			return originalFetch(url, options);
		}
		
		// Skip auth handling for login/logout requests
		if (url.includes('/api/auth/login') || 
			url.includes('/api/auth/logout') || 
			url.includes('/api/oauth/logout')) {
			return originalFetch(url, options);
		}

		// Skip auth handling for OTP requests
		if (url.includes('/api/oauth/send-email-otp') || 
			url.includes('/api/oauth/verify-email-otp') || 
			url.includes('/api/oauth/resend-email-otp')) {
			return originalFetch(url, options);
		}
		
		// Skip token refresh if we're on the login page (to break infinite loops)
		if (sessionStorage.getItem('on_login_page') === 'true' && url.includes('/api/user/me')) {
			console.log('Skipping auth for /api/user/me on login page to prevent refresh loops');
			return originalFetch(url, options);
		}

		// Don't try to refresh if we're already logging out
		if (loggingOut) {
			return originalFetch(url, options);
		}

		// Don't try to refresh if we're on the character list page
		if (url.includes('/api/character')) {
			return originalFetch(url, options);
		}

		try {
			// Make the original request
			const response = await originalFetch(url, options);
			
			// If successful, return the response immediately
			if (response.ok) {
				return response;
			}
			
			// If unauthorized and we're not already refreshing the token or logging out,
			// try refreshing the token once
			if (response.status === 401 && !isRefreshingToken && !loggingOut) {
				console.log('401 response received, attempting token refresh');
				
				// Set flag to prevent multiple refresh attempts
				isRefreshingToken = true;
				
				try {
					// Try to refresh the token
					const refreshSuccessful = await refreshToken();
					
					// Reset the refresh flag
					isRefreshingToken = false;
					
					if (refreshSuccessful) {
						console.log('Token refreshed successfully, retrying request');
						
						// Retry the original request with the fresh token
						return originalFetch(url, options);
					}
				} catch (error) {
					console.error('Error during token refresh:', error);
					isRefreshingToken = false;
				}
				
				// If we reach here, token refresh failed
				if (!isRedirectingToLogin && !isLoggingOut) {
					console.log('Token refresh failed, clearing user state');
					
					// Clear the user state
					store.dispatch('clearUser');
					
					// Only redirect to login if the current route requires authentication
					const currentRoute = window.router?.currentRoute?.value;
					const isAuthRequired = currentRoute?.matched?.some(record => record.meta.requiresAuth);
					
					if (isAuthRequired) {
						console.log('Current route requires auth, redirecting to login');
						
						// Prevent multiple redirects and logout attempts
						isRedirectingToLogin = true;
						setLoggingOut(true);
						
						try {
							// Logout the user
							await store.dispatch('logout');
						} catch (logoutError) {
							console.error('Error during logout:', logoutError);
							// Force redirect to login even if logout API fails
							window.location.href = '/';
						} finally {
							// Reset flags after a delay
							setTimeout(() => {
								isRedirectingToLogin = false;
								setLoggingOut(false);
							}, 2000);
						}
					} else {
						console.log('Current route does not require auth, staying on page');
					}
				}
			}
			
			// Return the original response if we can't refresh
			return response;
		} catch (error) {
			console.error('Fetch error:', error);
			throw error;
		}
	};
	
	console.log('Auth interceptor setup complete');
}
